import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getMyOrder } from "@/lib/account/account.functions";
import { getInvoiceForOrder, issueInvoiceForOrder, getInvoiceDownloadUrl } from "@/lib/account/billing.functions";
import {
  ArrowLeft, Truck, Clock, Package, CheckCircle2, XCircle, RotateCcw, Copy, ExternalLink,
  FileText, Download, Loader2, ReceiptText, CreditCard, MapPin, Mail, Phone, ShoppingBag,
  Printer, Sparkles, LifeBuoy, Hash,
} from "lucide-react";
import { toast } from "sonner";
import { useMemo, useState } from "react";
import { useCart } from "@/lib/cart/cart-store";

export const Route = createFileRoute("/_account/account/orders/$id")({
  component: OrderDetailPage,
});

const stepMeta: Record<string, { label: string; icon: any; tone: string; ring: string }> = {
  pending_payment: { label: "Awaiting payment", icon: Clock, tone: "text-slate-700 bg-slate-100", ring: "ring-slate-200" },
  paid: { label: "Payment received", icon: CreditCard, tone: "text-sky-700 bg-sky-100", ring: "ring-sky-200" },
  started: { label: "In production", icon: Package, tone: "text-amber-700 bg-amber-100", ring: "ring-amber-200" },
  shipped: { label: "Shipped", icon: Truck, tone: "text-emerald-700 bg-emerald-100", ring: "ring-emerald-200" },
  cancelled: { label: "Cancelled", icon: XCircle, tone: "text-zinc-700 bg-zinc-100", ring: "ring-zinc-200" },
  returned: { label: "Returned", icon: RotateCcw, tone: "text-rose-700 bg-rose-100", ring: "ring-rose-200" },
};

const pipeline = ["paid", "started", "shipped"] as const;

function carrierUrl(c?: string | null, n?: string | null) {
  if (!c || !n) return null;
  const k = c.toLowerCase();
  if (k.includes("dhl")) return `https://www.dhl.com/global-en/home/tracking.html?tracking-id=${encodeURIComponent(n)}`;
  if (k.includes("ups")) return `https://www.ups.com/track?tracknum=${encodeURIComponent(n)}`;
  if (k.includes("fedex")) return `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(n)}`;
  if (k.includes("speedy")) return `https://www.speedy.bg/en/track-shipment?shipmentNumber=${encodeURIComponent(n)}`;
  if (k.includes("econt")) return `https://www.econt.com/services/track-shipment/${encodeURIComponent(n)}`;
  return null;
}

function countryFlag(iso?: string | null) {
  if (!iso || iso.length !== 2) return "";
  const A = 0x1f1e6;
  return String.fromCodePoint(A + iso.toUpperCase().charCodeAt(0) - 65, A + iso.toUpperCase().charCodeAt(1) - 65);
}

function eur(v: number | string | null | undefined) {
  const n = Number(v ?? 0);
  return `${n.toFixed(2)} €`;
}

function CopyBtn({ text, label }: { text: string; label?: string }) {
  const [ok, setOk] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text);
        setOk(true);
        toast.success(`${label ?? "Copied"} copied`);
        setTimeout(() => setOk(false), 1400);
      }}
      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
    >
      {ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
      {label ?? "Copy"}
    </button>
  );
}

function OrderDetailPage() {
  const { id } = Route.useParams();
  const get = useServerFn(getMyOrder);
  const q = useQuery({ queryKey: ["account", "order", id], queryFn: () => get({ data: { id } }) });
  const add = useCart((s) => s.add);
  const openCart = useCart((s) => s.open);

  const reorder = () => {
    const items = (q.data?.items ?? []) as any[];
    let added = 0;
    for (const it of items) {
      if (!it.variant_id || !it.product_slug) continue;
      add({
        productId: it.variant_id, // fallback identifier
        variantId: it.variant_id,
        sku: it.product_sku,
        slug: it.product_slug,
        title: it.product_name,
        image: it.image_url,
        unitPriceCents: Math.round(Number(it.unit_price_eur) * 100),
        qty: it.quantity,
        currency: "EUR",
      } as any);
      added++;
    }
    if (added === 0) {
      toast.error("No re-orderable items in this order");
    } else {
      toast.success(`Added ${added} item${added === 1 ? "" : "s"} to your cart`);
      openCart();
    }
  };

  if (q.isLoading) {
    return (
      <div className="grid place-items-center rounded-2xl bg-white p-16 text-sm text-slate-500">
        <Loader2 className="mb-3 h-6 w-6 animate-spin text-slate-400" />
        Loading order…
      </div>
    );
  }
  const notFound = q.error instanceof Error && /not found/i.test(q.error.message);
  if (!q.data && q.error && !notFound) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
        Something went wrong loading this order.
        <button onClick={() => q.refetch()} className="ml-2 font-semibold underline">Retry</button>
      </div>
    );
  }
  if (!q.data) {
    return (
      <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-900">
        Order not found.{" "}
        <Link to="/account/orders" className="font-semibold underline">Back to orders</Link>
      </div>
    );
  }

  const { order, items, history } = q.data as any;
  const status: string = order.status;
  const m = stepMeta[status] ?? stepMeta.paid;
  const StatusIcon = m.icon;
  const currentStep = pipeline.indexOf(status as any);
  const isSide = status === "cancelled" || status === "returned";
  const trackUrl = carrierUrl(order.tracking_carrier, order.tracking_number);
  const flag = countryFlag(order.country);

  const supportSubject = encodeURIComponent(`Order #${order.order_number}`);
  const supportBody = encodeURIComponent(`Hi FIXHEN team,\n\nRegarding order #${order.order_number} placed on ${new Date(order.created_at).toLocaleDateString()}, I have a question:\n\n`);
  const supportMailto = `mailto:info@contact.fixhen.com?subject=${supportSubject}&body=${supportBody}`;

  return (
    <div className="space-y-5 print:space-y-3">
      {/* Sticky sub-header with actions */}
      <div className="sticky top-0 z-10 -mx-1 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white/90 px-4 py-3 backdrop-blur print:hidden">
        <div className="flex min-w-0 items-center gap-3">
          <Link
            to="/account/orders"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All orders
          </Link>
          <div className="min-w-0">
            <p className="truncate text-sm font-bold text-slate-900">Order #{order.order_number}</p>
            <span className={`mt-0.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.tone}`}>
              <StatusIcon className="h-3 w-3" /> {m.label}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <CopyBtn text={String(order.order_number)} label="Order #" />
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <Printer className="h-3.5 w-3.5" /> Print
          </button>
          <button
            onClick={reorder}
            className="inline-flex items-center gap-1 rounded-lg bg-[#2563EB] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1d4ed8]"
          >
            <ShoppingBag className="h-3.5 w-3.5" /> Reorder
          </button>
        </div>
      </div>

      {/* Hero */}
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-[#0B1B4D] via-[#172554] to-[#2563EB] p-6 text-white shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-white/60">Order</p>
            <h1 className="mt-1 text-3xl font-black tracking-tight sm:text-4xl">#{order.order_number}</h1>
            <p className="mt-1 text-sm text-white/70">
              Placed {new Date(order.created_at).toLocaleString()}
              {order.paid_at && ` · Paid ${new Date(order.paid_at).toLocaleString()}`}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ring-white/20`}>
                <StatusIcon className="h-3.5 w-3.5" /> {m.label}
              </span>
              {order.currency && (
                <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ring-white/20">
                  {order.currency.toUpperCase()}
                </span>
              )}
              {order.country && (
                <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ring-white/20">
                  {flag} {order.country}
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className="text-4xl font-black tracking-tight">{eur(order.total_eur)}</p>
            <p className="text-xs text-white/70">incl. shipping{Number(order.tax_eur) > 0 ? " + VAT" : ""}</p>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</h2>
        {isSide ? (
          <div className={`mt-4 flex items-center gap-3 rounded-xl p-4 ${m.tone}`}>
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-white/70">
              <StatusIcon className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold">{m.label}</p>
              <p className="text-xs opacity-80">
                {status === "cancelled" && order.cancelled_at && new Date(order.cancelled_at).toLocaleString()}
                {status === "returned" && order.returned_at && new Date(order.returned_at).toLocaleString()}
              </p>
              {order.stripe_refund_id && (
                <p className="mt-1 text-xs opacity-80">Refund issued via Stripe.</p>
              )}
            </div>
          </div>
        ) : (
          <div className="mt-6">
            <div className="relative">
              <div className="absolute left-5 top-5 h-[calc(100%-2.5rem)] w-0.5 bg-slate-200" />
              <div
                className="absolute left-5 top-5 w-0.5 bg-[#2563EB] transition-all"
                style={{ height: `calc((100% - 2.5rem) * ${Math.max(currentStep, 0) / (pipeline.length - 1)})` }}
              />
              <ul className="relative space-y-6">
                {pipeline.map((s, idx) => {
                  const sm = stepMeta[s];
                  const Icon = sm.icon;
                  const done = idx <= currentStep;
                  const ts = s === "paid" ? order.paid_at : s === "started" ? order.started_at : order.shipped_at;
                  return (
                    <li key={s} className="flex items-start gap-4">
                      <div className={`relative z-10 grid h-10 w-10 shrink-0 place-items-center rounded-full ring-4 ring-white ${done ? "bg-[#2563EB] text-white" : "bg-slate-100 text-slate-400"}`}>
                        {done && idx < currentStep ? <CheckCircle2 className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                      </div>
                      <div className="min-w-0 flex-1 pt-1.5">
                        <p className={`text-sm font-semibold ${done ? "text-slate-900" : "text-slate-500"}`}>{sm.label}</p>
                        <p className="text-xs text-slate-500">{ts ? new Date(ts).toLocaleString() : done ? "—" : "Pending"}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            {order.tracking_number && (
              <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                <div className="min-w-0">
                  <p className="text-xs font-medium uppercase tracking-wide text-emerald-700">Tracking</p>
                  <p className="truncate font-mono text-sm font-semibold text-emerald-900">
                    {order.tracking_carrier} · {order.tracking_number}
                  </p>
                  {order.shipped_at && (
                    <p className="text-xs text-emerald-800/80">
                      Shipped {new Date(order.shipped_at).toLocaleDateString()} · est. delivery in 2–4 business days
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 gap-2">
                  <CopyBtn text={order.tracking_number} label="Number" />
                  {trackUrl && (
                    <a
                      href={trackUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                    >
                      Track <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Items + Totals */}
      <section className="grid gap-5 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <div className="min-w-0 rounded-2xl border border-slate-200 bg-white">
          <header className="flex items-center justify-between border-b border-slate-100 p-5">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Items ({items.length})</h2>
            <span className="text-xs text-slate-400">SKU · Qty · Line</span>
          </header>
          <ul className="divide-y divide-slate-100">
            {items.map((it: any) => {
              const inner = (
                <>
                  {it.image_url ? (
                    <img
                      src={it.image_url}
                      alt=""
                      className="h-14 w-14 shrink-0 rounded-lg border border-slate-200 bg-slate-50 object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-slate-100 font-mono text-xs text-slate-500">
                      {(it.product_sku ?? "—").toString().slice(0, 4)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-slate-900">{it.product_name}</p>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500">
                      {it.product_sku && <span className="font-mono">SKU {it.product_sku}</span>}
                      {it.oem && <span className="font-mono">OEM {it.oem}</span>}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-sm font-semibold text-slate-900">{eur(it.line_total_eur ?? Number(it.unit_price_eur) * it.quantity)}</p>
                    <p className="text-xs text-slate-500">{eur(it.unit_price_eur)} × {it.quantity}</p>
                  </div>
                </>
              );
              return (
                <li key={it.id}>
                  {it.product_slug ? (
                    <Link
                      to={"/p/$slug" as never}
                      params={{ slug: it.product_slug } as never}
                      className="flex items-center gap-4 p-4 transition hover:bg-slate-50"
                    >
                      {inner}
                    </Link>
                  ) : (
                    <div className="flex items-center gap-4 p-4">{inner}</div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        <div className="min-w-0 space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Totals</h3>
            <div className="mt-3 space-y-1.5 text-sm">
              <Row label="Subtotal" value={eur(order.subtotal_eur)} />
              <Row label="Shipping" value={eur(order.shipping_eur)} />
              {Number(order.tax_eur) > 0 && <Row label="VAT" value={eur(order.tax_eur)} />}
              {order.referral_code && (
                <Row label={`Referral (${order.referral_code})`} value="applied" muted />
              )}
              <div className="my-2 h-px bg-slate-100" />
              <Row label="Total" value={eur(order.total_eur)} bold />
              {order.stripe_refund_id && (
                <p className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-800">
                  Refunded via Stripe
                </p>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-[#2563EB]" />
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Payment</h3>
            </div>
            <dl className="mt-3 space-y-1.5 text-sm">
              <KV k="Method" v="Stripe Checkout" />
              <KV k="Status" v={order.paid_at ? "Paid" : status === "pending_payment" ? "Awaiting payment" : status} />
              {order.paid_at && <KV k="Paid on" v={new Date(order.paid_at).toLocaleString()} />}
              {order.stripe_payment_intent && (
                <div className="flex items-center justify-between gap-2">
                  <dt className="text-slate-500">Reference</dt>
                  <dd className="flex min-w-0 items-center gap-1.5">
                    <span className="truncate font-mono text-xs text-slate-700">…{String(order.stripe_payment_intent).slice(-10)}</span>
                    <CopyBtn text={order.stripe_payment_intent} label="ID" />
                  </dd>
                </div>
              )}
              <KV k="Currency" v={(order.currency ?? "EUR").toUpperCase()} />
            </dl>
          </div>
        </div>
      </section>

      {/* Shipping + History */}
      <section className="grid gap-5 md:grid-cols-2">
        <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-[#2563EB]" />
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Ship to</h3>
          </div>
          <p className="mt-3 font-semibold text-slate-900">{order.customer_name}</p>
          {order.company_name && <p className="text-sm text-slate-700">{order.company_name}</p>}
          <div className="mt-1 space-y-0.5 text-sm text-slate-600">
            {order.shipping_line1 && <p>{order.shipping_line1}</p>}
            {order.shipping_line2 && <p>{order.shipping_line2}</p>}
            {(order.shipping_postal_code || order.shipping_city) && (
              <p>{[order.shipping_postal_code, order.shipping_city].filter(Boolean).join(" ")}</p>
            )}
            {!order.shipping_line1 && order.shipping_address && (
              <p className="whitespace-pre-line">{order.shipping_address}</p>
            )}
            <p>{flag} {order.country}</p>
          </div>
          <div className="mt-3 flex flex-wrap gap-3 border-t border-slate-100 pt-3 text-xs text-slate-600">
            {order.customer_email && (
              <span className="inline-flex min-w-0 items-center gap-1">
                <Mail className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                <span className="truncate">{order.customer_email}</span>
              </span>
            )}
            {order.phone && (
              <span className="inline-flex items-center gap-1">
                <Phone className="h-3.5 w-3.5 text-slate-400" /> {order.phone}
              </span>
            )}
          </div>
          {order.notes && (
            <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50 p-3 text-xs text-slate-700">
              <p className="mb-1 font-semibold text-slate-500">Customer note</p>
              {order.notes}
            </div>
          )}
        </div>

        <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2">
            <Hash className="h-4 w-4 text-[#2563EB]" />
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Activity</h3>
          </div>
          <ul className="mt-3 space-y-3">
            <li className="flex items-start gap-2 text-sm">
              <div className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-slate-100 text-slate-500">
                <Sparkles className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0">
                <p className="text-slate-900">Order placed</p>
                <p className="text-xs text-slate-500">{new Date(order.created_at).toLocaleString()}</p>
              </div>
            </li>
            {(history as any[]).map((h) => {
              const to = stepMeta[h.to_status];
              const Icon = to?.icon ?? CheckCircle2;
              return (
                <li key={h.id} className="flex items-start gap-2 text-sm">
                  <div className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full ${to?.tone ?? "bg-slate-100 text-slate-500"}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-slate-900">
                      {(stepMeta[h.from_status]?.label ?? h.from_status)} → {(stepMeta[h.to_status]?.label ?? h.to_status)}
                    </p>
                    <p className="text-xs text-slate-500">{new Date(h.changed_at).toLocaleString()}</p>
                  </div>
                </li>
              );
            })}
            {history.length === 0 && (
              <li className="text-xs text-slate-500">No status changes yet.</li>
            )}
          </ul>
        </div>
      </section>

      {/* Invoice */}
      <InvoiceSection orderId={order.id} paid={!!order.paid_at} />

      {/* Support */}
      <section className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-gradient-to-r from-sky-50 to-white p-5 print:hidden">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#2563EB]/10 text-[#2563EB]">
            <LifeBuoy className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">Need help with this order?</p>
            <p className="text-xs text-slate-600">Our team usually replies within a few hours on business days.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <a
            href={supportMailto}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <Mail className="h-3.5 w-3.5" /> Email us
          </a>
          <Link
            to="/contact"
            className="inline-flex items-center gap-1 rounded-lg bg-[#2563EB] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1d4ed8]"
          >
            Contact form
          </Link>
        </div>
      </section>
    </div>
  );
}

function Row({ label, value, bold, muted }: { label: string; value: string; bold?: boolean; muted?: boolean }) {
  return (
    <div className={`flex justify-between gap-3 ${bold ? "pt-1 text-base font-bold text-slate-900" : muted ? "text-slate-500" : "text-slate-600"}`}>
      <span className="min-w-0 truncate">{label}</span>
      <span className="shrink-0 text-right">{value}</span>
    </div>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-slate-500">{k}</dt>
      <dd className="min-w-0 truncate text-slate-900">{v}</dd>
    </div>
  );
}

function InvoiceSection({ orderId, paid }: { orderId: string; paid: boolean }) {
  const qc = useQueryClient();
  const getInv = useServerFn(getInvoiceForOrder);
  const issue = useServerFn(issueInvoiceForOrder);
  const getUrl = useServerFn(getInvoiceDownloadUrl);
  const [downloading, setDownloading] = useState(false);

  const invQ = useQuery({ queryKey: ["account", "order", orderId, "invoice"], queryFn: () => getInv({ data: { order_id: orderId } }) });
  const issueM = useMutation({
    mutationFn: () => issue({ data: { order_id: orderId } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["account", "order", orderId, "invoice"] });
      qc.invalidateQueries({ queryKey: ["account", "billing", "invoices"] });
      toast.success("Invoice issued");
    },
    onError: (e: any) => toast.error(e?.message ?? "Error"),
  });

  async function download(id: string) {
    try {
      setDownloading(true);
      const r = await getUrl({ data: { invoice_id: id } });
      const a = document.createElement("a");
      a.href = r.url;
      a.download = r.filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e: any) {
      toast.error(e?.message ?? "Unable to download");
    } finally { setDownloading(false); }
  }

  const inv = invQ.data;
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6">
      <div className="flex items-center gap-2">
        <ReceiptText className="h-4 w-4 text-[#2563EB]" />
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Invoice</h2>
      </div>
      {invQ.isLoading ? (
        <p className="mt-3 text-sm text-slate-500">Loading…</p>
      ) : inv ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-slate-100 text-slate-500">
              <FileText className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-900">{inv.invoice_number}</p>
              <p className="truncate text-xs text-slate-500">
                Issued {new Date(inv.issued_at).toLocaleDateString()} · {(inv.total_cents / 100).toFixed(2)} {inv.currency}
              </p>
            </div>
          </div>
          <button
            onClick={() => download(inv.id)}
            disabled={downloading}
            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Download PDF
          </button>
        </div>
      ) : paid ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-600">Generate an invoice for this order using your default billing profile.</p>
          <button
            onClick={() => issueM.mutate()}
            disabled={issueM.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#2563EB] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1d4ed8] disabled:opacity-50"
          >
            {issueM.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ReceiptText className="h-3.5 w-3.5" />} Issue invoice
          </button>
        </div>
      ) : (
        <p className="mt-3 text-sm text-slate-500">Invoice becomes available after payment is received.</p>
      )}
    </section>
  );
}
